import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { CalendarComponent } from '@syncfusion/ej2-react-calendars';
import axios from 'axios';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import LogoLoader from './LogoLoader';
import ExpertChatIcon from '../Images/expert-chat-icon.svg';
import ExpertChatIconWhite from '../Images/expert-chat-icon-white.svg';

// ============================================
// ANIMATIONS
// ============================================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(100%); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(45, 91, 237, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(45, 91, 237, 0); }
  100% { box-shadow: 0 0 0 0 rgba(45, 91, 237, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================
// GLASSMORPHISM BASE COMPONENTS
// ============================================
const GlassPanel = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid #edf1f9;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(39, 68, 131, 0.105);
`;

// ============================================
// TIME SLOT CARD - Modern Glassmorphism Design
// ============================================
const Slot = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    width: 100%;
    border: 1px solid ${props => props.selected 
      ? 'rgba(45, 91, 237, 0.5)' 
      : '#edf1f9'};
    border-radius: 12px;
    background: ${props => props.selected 
      ? 'linear-gradient(135deg, rgba(45, 91, 237, 0.3), rgba(41, 169, 221, 0.2))' 
      : '#f8faff'};
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #192947;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 10px;
    position: relative;
    overflow: hidden;
    box-shadow: ${props => props.selected 
      ? '0 8px 24px rgba(45, 91, 237, 0.25), inset 0 1px 0 rgba(39, 68, 131, 0.05)' 
      : '0 4px 12px rgba(39, 68, 131, 0.053), inset 0 1px 0 rgba(39, 68, 131, 0.025)'};

    /* Selected checkmark indicator */
    ${props => props.selected && css`
      &::after {
        content: '✓';
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        background: linear-gradient(135deg, #2d5bed, #29a9dd);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        animation: ${fadeIn} 0.3s ease;
      }
    `}

    .time-display {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .time-value {
      font-weight: 600;
      font-size: 15px;
      color: ${props => props.selected ? '#192947' : '#2c64da'};
      letter-spacing: 0.3px;
    }

    .time-separator {
      color: #8f9db3;
      font-size: 12px;
    }

    .time-label {
      font-size: 11px;
      color: #7b8aa3;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-right: 6px;
    }

    &:hover {
      background: ${props => props.selected 
        ? 'linear-gradient(135deg, rgba(45, 91, 237, 0.4), rgba(41, 169, 221, 0.3))' 
        : '#f5f8fd'};
      border-color: ${props => props.selected 
        ? 'rgba(45, 91, 237, 0.6)' 
        : '#e0e8f5'};
      transform: translateY(-2px) scale(1.01);
      box-shadow: 0 12px 28px rgba(39, 68, 131, 0.088);
    }

    &:active {
      transform: translateY(0) scale(0.99);
    }
    
    @media (max-width: 768px) {
      padding: 14px 16px;
      margin-bottom: 8px;
      
      .time-display {
        flex: 1;
        justify-content: space-between;
      }
      
      ${props => props.selected && css`
        &::after {
          right: 12px;
          width: 22px;
          height: 22px;
        }
      `}
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-left: 0px;
    padding: 0;
    max-height: 380px;
    overflow-y: auto;
    width: 100%;
    position: relative;
    opacity: 0;
    transition: opacity 0.4s ease, transform 0.4s ease;
    transform: translateY(10px);
    padding: 12px 8px;
    
    ${props => props.$showSlots && css`
        opacity: 1;
        transform: translateY(0);
    `}
    
    @media (max-width: 768px) {
        /* On phones the list scrolls inside the accordion: with max-height:none the accordion's own
           height cap cut it off and there was nothing to scroll (29 slots, only ~6 reachable). */
        width: 100%;
        max-height: 60vh;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        padding: 10px 8px 16px;
        margin-top: 0;
    }

    /* Custom Scrollbar */
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: #f8faff;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(45, 91, 237, 0.3), rgba(41, 169, 221, 0.3));
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(45, 91, 237, 0.5), rgba(41, 169, 221, 0.5));
    }
`;

const ConfirmButton = styled.button`
    padding: 14px 32px;
    background: linear-gradient(135deg, #2d5bed, #29a9dd);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.5px;
    box-shadow: 0 4px 15px rgba(45, 91, 237, 0.35);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, #e6ecf6, transparent);
        transition: left 0.5s;
    }

    &:hover {
        background: linear-gradient(135deg, #4a8be5, #10e2ff);
        box-shadow: 0 8px 25px rgba(45, 91, 237, 0.45);
        transform: translateY(-2px);
        
        &::before {
            left: 100%;
        }
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        background: linear-gradient(135deg, rgba(45, 91, 237, 0.4), rgba(41, 169, 221, 0.4));
        cursor: not-allowed;
        box-shadow: none;
        transform: none;
    }
`;

const CancelConfirmButton = styled.button`
    padding: 14px 32px;
    background: #f5f8fd;
    color: #33445e;
    border: 1px solid #e0e8f5;
    border-radius: 10px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.5px;
    backdrop-filter: blur(4px);

    &:hover {
        background: #edf2ff;
        color: #192947;
        border-color: #cad5e9;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    width: 100%;
    margin-top: 20px;
    
    @media (max-width: 480px) {
        flex-direction: column;
    }
`;

const ConfirmMessageWrapper = styled.div`
    display: none;
    
    @media (min-width: 769px) {
        display: none;
    }
`;

// ============================================
// MEETING INFO MODAL COMPONENTS (Redesigned)
// ============================================

const MeetingInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 8px;
    animation: ${fadeIn} 0.4s ease-out;
    
    @media (max-width: 768px) {
        gap: 20px;
        padding: 4px;
    }
`;

const MeetingHeader = styled.div`
    text-align: center;
    margin-bottom: 8px;
    
    .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, rgba(31, 157, 107, 0.2), rgba(31, 157, 107, 0.1));
        border: 1px solid rgba(31, 157, 107, 0.3);
        border-radius: 20px;
        padding: 8px 16px;
        color: #0f8f63;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.5px;
        
        .status-dot {
            width: 8px;
            height: 8px;
            background: #1f9d6b;
            border-radius: 50%;
            animation: ${pulse} 2s infinite;
        }
    }
    
    @media (max-width: 480px) {
        .status-badge {
            padding: 6px 12px;
            font-size: 12px;
        }
    }
`;

const MeetingInfoContainer = styled.div`
    background: linear-gradient(135deg, rgba(243, 246, 249, 0.95), rgba(243, 245, 249, 0.95));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    padding: 0;
    border-radius: 20px;
    color: #192947;
    border: 1px solid #e0e8f5;
    box-shadow: 0 8px 32px rgba(39, 68, 131, 0.105);
    overflow: hidden;
    
    @media (max-width: 768px) {
        border-radius: 16px;
    }
`;

const MeetingDateCard = styled.div`
    background: linear-gradient(135deg, rgba(41, 169, 221, 0.15), rgba(41, 169, 221, 0.05));
    padding: 24px;
    display: flex;
    align-items: center;
    gap: 20px;
    border-bottom: 1px solid #edf1f9;
    
    .calendar-icon {
        width: 64px;
        height: 64px;
        background: linear-gradient(135deg, #29a9dd, #335cf4);
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 16px rgba(41, 169, 221, 0.3);
        flex-shrink: 0;
        
        .month {
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #192947;
            margin-bottom: 2px;
        }
        
        .day {
            font-size: 24px;
            font-weight: 700;
            color: #192947;
            line-height: 1;
        }
    }
    
    .date-details {
        flex: 1;
        
        .weekday {
            font-size: 14px;
            color: #65748d;
            margin-bottom: 4px;
        }
        
        .full-date {
            font-size: 18px;
            font-weight: 600;
            color: #192947;
        }
    }
    
    @media (max-width: 480px) {
        padding: 18px;
        gap: 16px;
        
        .calendar-icon {
            width: 56px;
            height: 56px;
            border-radius: 12px;
            
            .month {
                font-size: 9px;
            }
            
            .day {
                font-size: 20px;
            }
        }
        
        .date-details {
            .weekday {
                font-size: 13px;
            }
            
            .full-date {
                font-size: 16px;
            }
        }
    }
`;

const MeetingTimeRow = styled.div`
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 16px;
    
    .time-icon {
        width: 44px;
        height: 44px;
        background: #f5f8fd;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        flex-shrink: 0;
    }
    
    .time-content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 12px;
        
        .time-block {
            .label {
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 1px;
                color: #7b8aa3;
                margin-bottom: 4px;
            }
            
            .time {
                font-size: 18px;
                font-weight: 600;
                color: #192947;
            }
        }
        
        .time-separator {
            font-size: 20px;
            color: #a9b5c8;
            padding: 0 4px;
        }
    }
    
    .duration-badge {
        background: #edf2ff;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: 12px;
        color: #455776;
        white-space: nowrap;
    }
    
    @media (max-width: 480px) {
        padding: 16px 18px;
        flex-wrap: wrap;
        gap: 12px;
        
        .time-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
        }
        
        .time-content {
            flex: 1;
            min-width: 0;
            
            .time-block .time {
                font-size: 16px;
            }
        }
        
        .duration-badge {
            width: 100%;
            text-align: center;
            margin-top: 4px;
        }
    }
`;

const MeetingQuickActions = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #edf1f9;
    background: rgba(25, 41, 71, 0.025);
    
    @media (max-width: 480px) {
        padding: 14px 18px;
        gap: 10px;
    }
`;

const QuickActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #f5f8fd;
    border: 1px solid #edf1f9;
    border-radius: 12px;
    padding: 12px 20px;
    color: #33445e;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.25s ease;
    
    .icon {
        font-size: 16px;
    }
    
    &:hover {
        background: rgba(41, 169, 221, 0.15);
        border-color: rgba(41, 169, 221, 0.3);
        color: #2c64da;
        transform: translateY(-2px);
    }
    
    @media (max-width: 480px) {
        flex: 1;
        padding: 10px 14px;
        font-size: 12px;
    }
`;

const MeetingInfoButtonsContainer = styled.div`
    display: flex;
    gap: 12px;
    
    @media (max-width: 480px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const CancelButton = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: linear-gradient(135deg, rgba(229, 72, 77, 0.15), rgba(229, 72, 77, 0.08));
    color: #d63c3c;
    border: 1px solid rgba(229, 72, 77, 0.25);
    border-radius: 14px;
    padding: 16px 24px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.3s ease;
    letter-spacing: 0.3px;

    .icon {
        font-size: 18px;
    }

    &:hover {
        background: linear-gradient(135deg, rgba(229, 72, 77, 0.25), rgba(229, 72, 77, 0.15));
        border-color: rgba(229, 72, 77, 0.4);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(229, 72, 77, 0.2);
    }
    
    &:active {
        transform: translateY(0);
    }

    @media (max-width: 480px) {
        padding: 14px 20px;
        font-size: 14px;
    }
`;

const RescheduleButton = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: linear-gradient(135deg, rgba(41, 169, 221, 0.2), rgba(41, 169, 221, 0.1));
    color: #2c64da;
    border: 1px solid rgba(41, 169, 221, 0.3);
    border-radius: 14px;
    padding: 16px 24px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.3s ease;
    letter-spacing: 0.3px;

    .icon {
        font-size: 18px;
    }

    &:hover {
        background: linear-gradient(135deg, rgba(41, 169, 221, 0.3), rgba(41, 169, 221, 0.2));
        border-color: rgba(41, 169, 221, 0.5);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(41, 169, 221, 0.2);
    }
    
    &:active {
        transform: translateY(0);
    }

    @media (max-width: 480px) {
        padding: 14px 20px;
        font-size: 14px;
    }
`;

// Legacy containers (kept for compatibility, can be removed if not used elsewhere)
const MeetingDateContainer = styled.div`
    display: none;
`;

const MeetingTimeContainer = styled.div`
    display: none;
`;

const DayNumber = styled.span`
  color: #192947;  // Increased opacity from likely 0.7 to 0.95
  font-size: 12px;
  font-weight: 400;
`;

// If you have a specific day selected, you might also want to update its styling:
const SelectedDayNumber = styled(DayNumber)`
  color: #192947;  // Full brightness for selected day
  font-weight: 500;
`;

const CalendarContainer = styled(GlassPanel)`
  width: 55%;
  padding: 20px;
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 0 auto;
    border-radius: 16px;
  }
  
  .e-calendar {
    background: transparent !important;
    border: none !important;
    width: 100%;
    box-shadow: none !important;
    
    @media (max-width: 768px) {
      width: 100%;
      max-width: none;
    }
    
    .e-btn.e-primary,
    .e-css.e-btn.e-primary {
      background: linear-gradient(135deg, #2d5bed, #29a9dd) !important;
      border-color: transparent !important;
      color: #ffffff !important;
      box-shadow: 0 4px 12px rgba(45, 91, 237, 0.3) !important;
      border-radius: 8px !important;
      transition: all 0.3s ease !important;
      
      &:hover {
        box-shadow: 0 6px 16px rgba(45, 91, 237, 0.4) !important;
      }
    }
    
    /* Header styling */
    .e-header {
      background: transparent;
      padding: 8px 0 16px;
      
      .e-title {
        color: #192947 !important;
        font-size: 20px;
        font-weight: 600;
        letter-spacing: 0.5px;
      }
      
      .e-icon-container {
        .e-prev,
        .e-next,
        .e-date-icon-prev,
        .e-date-icon-next {
          color: #2c64da !important;
          font-weight: bold;
          transition: all 0.2s ease;
          
          &:hover {
            color: #192947 !important;
          }
        }
      }
    }
    
    /* Day names (Mon, Tue, etc.) styling */
    th {
      color: #65748d !important;
      border-bottom: 0;
      font-weight: 500;
      letter-spacing: 0.5px;
      font-size: 12px;
      text-transform: uppercase;
      padding: 12px 0;
    }
    
    /* Content styling */
    .e-content {
      td {
        color: #192947 !important;
        transition: all 0.2s ease;
        border-radius: 8px;
        
        &.e-selected {
          background: linear-gradient(135deg, #2d5bed, #29a9dd) !important;
          border-color: transparent !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(45, 91, 237, 0.4);

          span.e-day {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            color: #192947 !important;
          }
        }
        
        &.e-focused-date,
        &.e-current-focused {
          background: linear-gradient(135deg, #2d5bed, #29a9dd) !important;
          border-color: transparent !important;
          color: #ffffff !important;
        }
        
        &.e-today {
          border: 2px solid #2d5bed !important;
          color: #192947 !important;
          background-color: rgba(45, 91, 237, 0.1) !important;
          border-radius: 8px;

          span.e-day {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            color: #192947 !important;
            box-shadow: none !important;
          }

          &.e-focused-date span.e-day {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            color: #192947 !important;
            box-shadow: none !important;
          }
        }

        &:hover:not(.e-selected):not(.e-disabled) {
          background-color: rgba(45, 91, 237, 0.15) !important;
        }

        &.e-other-month {
          color: #a9b5c8 !important;
        }

        .e-day,
        span {
          color: inherit !important;
        }
      }
    }
  }
`;

const TimeSlot = ({ startTime, endTime }) => {
  return (
    <Slot>
      <div>{startTime}</div>
      <div>{endTime}</div>
    </Slot>
  );
};

const MeetingChatButton = styled.button`
  display: none; /* Hidden - using QuickActionButton instead */
`;

// ============================================
// MAIN LAYOUT COMPONENTS
// ============================================
const CalendarAndSlotsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 24px;
  flex: 1;
  min-height: 400px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
    min-height: auto;
    padding-bottom: 100px; /* Space for fixed bottom bar */
  }
`;

const DesktopSlotsContainer = styled(GlassPanel)`
  display: flex;
  flex-direction: column;
  width: 45%;
  padding: 20px;
  height: fit-content;
  max-height: 450px;
  animation: ${fadeIn} 0.4s ease;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSlotsSection = styled.div`
  display: none;
  width: 100%;
  
  @media (max-width: 768px) {
    display: block;
    margin-top: 0;
  }
`;

// Mobile Accordion Header
const MobileAccordionHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(12px);
    border: 1px solid #edf1f9;
    border-radius: 12px;
    margin-top: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.95);
      border-color: #e0e8f5;
    }
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  flex-direction: column;
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #192947;
    margin-bottom: 4px;
  }
  
  .subtitle {
    font-size: 13px;
    color: #65748d;
  }
  
  .slot-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(45, 91, 237, 0.3), rgba(41, 169, 221, 0.2));
    color: #2c64da;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 12px;
    margin-left: 8px;
  }
`;

const AccordionChevron = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #65748d;
  transition: transform 0.3s ease;
  
  ${props => props.$isOpen && css`
    transform: rotate(180deg);
  `}
`;

const MobileAccordionContent = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    /* tall enough for the list's own 60vh scroll area + padding; the list scrolls, not this box */
    max-height: ${props => props.$isOpen ? 'calc(60vh + 60px)' : '0'};
    overflow: hidden;
    transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(8px);
    border-radius: 0 0 12px 12px;
    margin-top: -12px;
    padding: ${props => props.$isOpen ? '20px 12px 12px' : '0 12px'};
  }
`;

const AvailableTimesTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid #edf1f9;
  
  .title-text {
    font-size: 16px;
    font-weight: 600;
    color: #192947;
    letter-spacing: 0.5px;
  }
  
  .slot-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(45, 91, 237, 0.3), rgba(41, 169, 221, 0.2));
    color: #2c64da;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;
  }
`;

const NoSlotsMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #65748d;
    text-align: center;
    padding: 40px 20px;
    font-size: 14px;
    background: rgba(25, 41, 71, 0.025);
    border-radius: 12px;
    border: 1px dashed #edf1f9;
    
    .icon {
      font-size: 32px;
      margin-bottom: 12px;
      opacity: 0.5;
    }
    
    .message {
      line-height: 1.5;
    }
`;

// ============================================
// FIXED BOTTOM CONFIRMATION BAR (Mobile)
// ============================================
const FixedConfirmBar = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$show ? 'flex' : 'none'};
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: 1px solid #edf1f9;
    padding: 16px 20px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    z-index: 1000;
    animation: ${slideUp} 0.3s ease;
    box-shadow: 0 -8px 32px rgba(39, 68, 131, 0.14);
  }
`;

const ConfirmBarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const ConfirmBarInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  .date {
    font-size: 14px;
    font-weight: 600;
    color: #192947;
    margin-bottom: 2px;
  }
  
  .time {
    font-size: 13px;
    color: #2c64da;
  }
`;

const ConfirmBarActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ConfirmBarButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #2d5bed, #29a9dd);
    border: none;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(45, 91, 237, 0.3);
    
    &:hover {
      box-shadow: 0 6px 16px rgba(45, 91, 237, 0.4);
    }
  ` : css`
    background: transparent;
    border: 1px solid #e0e8f5;
    color: #33445e;
    
    &:hover {
      background: #f5f8fd;
      border-color: #cad5e9;
    }
  `}
`;

// Desktop Inline Confirmation Panel
const DesktopConfirmPanel = styled.div`
  display: ${props => props.$show ? 'block' : 'none'};
  margin-top: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(45, 91, 237, 0.1), rgba(41, 169, 221, 0.05));
  border: 1px solid rgba(45, 91, 237, 0.3);
  border-radius: 12px;
  animation: ${fadeIn} 0.3s ease;
  
  .confirm-header {
    font-size: 14px;
    font-weight: 600;
    color: #192947;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .confirm-details {
    font-size: 13px;
    color: #33445e;
    margin-bottom: 16px;
    line-height: 1.6;
    
    strong {
      color: #2c64da;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// Refresh Button with icon
const RefreshButton = styled.button`
  background: #f8faff;
  color: #2c64da;
  border: 1px solid rgba(41, 169, 221, 0.15);
  border-radius: 10px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 0.3px;
  backdrop-filter: blur(4px);

  &::before {
    content: '↻';
    font-size: 16px;
    transition: transform 0.5s ease;
  }

  &:hover {
    background: rgba(45, 91, 237, 0.15);
    border-color: rgba(41, 169, 221, 0.3);
    transform: translateY(-2px);
    
    &::before {
      transform: rotate(180deg);
    }
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 12px;
  }
`;

// Error Message with animation
const ErrorMessage = styled.div`
  background: rgba(255, 77, 79, 0.1);
  color: #d63c3c;
  padding: 14px 18px;
  border-radius: 10px;
  margin-bottom: 16px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.15);
  border: 1px solid rgba(255, 77, 79, 0.2);
  display: flex;
  align-items: center;
  letter-spacing: 0.3px;
  line-height: 1.5;
  animation: ${fadeIn} 0.3s ease;
  backdrop-filter: blur(4px);
  
  &:before {
    content: "⚠";
    margin-right: 10px;
    font-size: 18px;
    color: #d63c3c;
  }
`;

// Selected Date Header (for mobile)
const SelectedDateHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(45, 91, 237, 0.15), rgba(41, 169, 221, 0.1));
    border-radius: 10px;
    margin-bottom: 12px;
    border: 1px solid rgba(45, 91, 237, 0.2);
    
    .date-text {
      font-size: 15px;
      font-weight: 600;
      color: #192947;
    }
    
    .day-name {
      font-size: 13px;
      color: #2c64da;
      margin-right: 8px;
    }
  }
`;

// Header Bar with title and refresh
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  
  .title {
    font-size: 20px;
    font-weight: 600;
    color: #192947;
    
    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
`;

const LoadingContainer = styled(GlassPanel)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 350px;
  width: 100%;
  position: relative;
  color: #192947;
`;

export const WeeklyCalendar = ({ expertId, closeWindow, isMettingScheduled, expertName,
  meetingInfo, finishSettingMeeting, threadId }) => {

  const [selectedValue, setSelectedValue] = useState(new Date().toLocaleDateString());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSolot, setSelectedSlot] = useState(null)
  const [loadFlag, setLoadFlag] = useState(false);
  const [localMeetingScheduled, setLocalMeetingScheduled] = useState(isMettingScheduled);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorMessageTimeout, setErrorMessageTimeout] = useState(null);
  const [mobileAccordionOpen, setMobileAccordionOpen] = useState(true);
  const authenticatedAxios = useAuthenticatedAxios();

  // Helper function to compare slots by value instead of reference
  const isSameSlot = (slot1, slot2) => {
    if (!slot1 || !slot2) return false;
    return slot1.start === slot2.start && slot1.end === slot2.end;
  };

  // Add reference to calendar component
  const calendarRef = React.useRef(null);

  // Add cleanup effect for calendar events
  useEffect(() => {
    return () => {
      // Clean up any event listeners when component unmounts
      if (calendarRef.current) {
        try {
          // Try to detach any events or cleanup the calendar
          const calendar = calendarRef.current;
          if (calendar && calendar.element) {
            // Attempt to clean up event listeners
            calendar.destroy?.();
          }
        } catch (error) {
          console.warn("Error during calendar cleanup:", error);
        }
      }
    };
  }, []);

  useEffect(() => {
    setLocalMeetingScheduled(isMettingScheduled);
  }, [isMettingScheduled]);

  // Effect to handle error message persistence
  useEffect(() => {
    // Clear any existing timeout
    if (errorMessageTimeout) {
      clearTimeout(errorMessageTimeout);
    }
    
    // If there's an error message, set a timeout to clear it after 3 seconds
    if (errorMessage) {
      const timeout = setTimeout(() => {
        setErrorMessage('');
        
        // After clearing the error message, refresh the available slots
        // Only do this for 409 errors (time slot no longer available)
        if (errorMessage.includes('no longer available')) {
          console.log('[DEBUG] Auto-refreshing slots after error message timeout');
          refreshAvailableSlots();
        }
      }, 3000); // 3 seconds
      
      setErrorMessageTimeout(timeout);
    }
    
    // Cleanup function to clear timeout when component unmounts
    return () => {
      if (errorMessageTimeout) {
        clearTimeout(errorMessageTimeout);
      }
    };
  }, [errorMessage]);

  // Filter out any slots that have been booked in the current session
  const filterOutBookedSlots = (slots) => {
    if (!bookedSlots.length) return slots;
    
    return slots.filter(slot => {
      // Check if this slot overlaps with any of our locally tracked booked slots
      return !bookedSlots.some(
        bookedSlot => {
          // Convert times to timestamps for easier comparison
          const slotStart = new Date(slot.start).getTime();
          const slotEnd = new Date(slot.end).getTime();
          const bookedStart = new Date(bookedSlot.start).getTime();
          const bookedEnd = new Date(bookedSlot.end).getTime();
          
          // Check for any overlap between the slots
          // Slot overlaps if:
          // 1. Slot starts during a booked slot (slotStart >= bookedStart && slotStart < bookedEnd)
          // 2. Slot ends during a booked slot (slotEnd > bookedStart && slotEnd <= bookedEnd)
          // 3. Slot completely contains a booked slot (slotStart <= bookedStart && slotEnd >= bookedEnd)
          return (
            (slotStart >= bookedStart && slotStart < bookedEnd) ||
            (slotEnd > bookedStart && slotEnd <= bookedEnd) ||
            (slotStart <= bookedStart && slotEnd >= bookedEnd)
          );
        }
      );
    });
  };

  const onchange = async (args) => {
    const date = new Date(args.value);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const formattedDate = `${month}/${day}/${year}`;

    // If the date hasn't changed and we already have slots, don't refetch
    // This prevents clearing selection when calendar re-fires change for same date
    if (formattedDate === selectedValue && availableSlots.length > 0) {
      console.log('[DEBUG] Same date selected, keeping current slots and selection');
      return;
    }

    // Don't set loading state here to avoid calendar focus issues
    setAvailableSlots([])
    setSelectedSlot(null)
    setErrorMessage(''); // Clear any error messages when changing dates

    setSelectedValue(formattedDate);
    
    console.log(`[DEBUG] Fetching available slots for date: ${formattedDate}, expertId: ${expertId}, threadId: ${threadId}`);

    try {
      // Add timestamp to prevent caching
      const response = await authenticatedAxios.get(
        `/api/experts/${expertId}/available-hours?date=${formattedDate}&threadId=${threadId}&_t=${new Date().getTime()}`
      );
      console.log(`[DEBUG] Available slots received from backend:`, response.data);
      console.log(`[DEBUG] Number of available slots: ${response.data.length}`);
      
      // Log each slot in a readable format
      if (response.data.length > 0) {
        console.log('[DEBUG] Available time slots:');
        response.data.forEach((slot, index) => {
          console.log(`[DEBUG] Slot ${index + 1}: ${new Date(slot.start).toLocaleTimeString()} - ${new Date(slot.end).toLocaleTimeString()}`);
        });
      } else {
        console.log('[DEBUG] No available slots returned from backend');
      }
      
      // Apply frontend filter to remove any slots we know are already booked
      const filteredSlots = filterOutBookedSlots(response.data);
      console.log(`[DEBUG] After frontend filtering: ${response.data.length - filteredSlots.length} slots removed`);
      
      // Remove slots that are already in the past (e.g. earlier today)
      const nowTs = Date.now();
      const futureSlots = filteredSlots.filter(slot => new Date(slot.start).getTime() > nowTs);
      console.log(`[DEBUG] After past-slot filtering: ${filteredSlots.length - futureSlots.length} past slots removed`);
      
      setAvailableSlots(futureSlots)
    } catch (error) {
      console.log('[DEBUG] Error fetching available slots:', error)
      setErrorMessage('Error loading available time slots. Please try again.');
    }
  };

  // Function to refresh available slots
  const refreshAvailableSlots = () => {
    if (selectedValue) {
      // Set loading flag here instead of in onchange
      setLoadFlag(true);
      setErrorMessage(''); // Clear any previous error messages
      
      const dateParts = selectedValue.split('/');
      const date = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);
      
      // Use setTimeout to avoid interfering with calendar focus
      setTimeout(() => {
        onchange({ value: date }).finally(() => {
          setLoadFlag(false);
        });
      }, 0);
    }
  };

  const timeSelected = async (slot) => {
    console.log(slot);
    setSelectedSlot(slot)
    // Clear error message when selecting a new slot
    setErrorMessage('');
  }

  const cancelSlotSelection = () => {
    setSelectedSlot(null);
  }

  const formatToLocalTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatToLocalDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  const confirmDate = async () => {
    // Block scheduling if the selected slot is in the past (extra safety)
    if (!selectedSolot || new Date(selectedSolot.start).getTime() <= Date.now()) {
      setErrorMessage('Cannot schedule a meeting in the past.');
      return;
    }

    setLoadFlag(true);
    setErrorMessage(''); // Clear any previous error messages
    
    try {
      console.log(`[DEBUG] Scheduling meeting with slot:`, selectedSolot);
      console.log(`[DEBUG] Meeting details - expertId: ${expertId}, expertName: ${expertName}, threadId: ${threadId}`);
      
      const res = await authenticatedAxios.post('/api/experts/meetings', {
        time: { start: selectedSolot.start, end: selectedSolot.end },
        expertId: expertId,
        expertName: expertName,
        threadId: threadId
      })
      console.log(`[DEBUG] Meeting scheduling response:`, res);
      
      // Track meeting creation in funnel
      try {
        const funnelSession = localStorage.getItem('imaven_funnel_session');
        if (funnelSession) {
          const session = JSON.parse(funnelSession);
          if (session.sessionId) {
            await authenticatedAxios.put(`/api/funnel/session/${session.sessionId}/meeting`, {
              meetingId: res.data?.meetingId || res.data?._id
            });
            console.log('[Funnel] Meeting creation tracked');
          }
        }
      } catch (funnelErr) {
        console.warn('[Funnel] Failed to track meeting:', funnelErr);
      }
      
      // Add this slot to our local tracking of booked slots
      setBookedSlots(prev => [...prev, selectedSolot]);
      
      // Refresh available slots after booking
      const date = new Date(selectedSolot.start);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${month}/${day}/${year}`;
      
      console.log(`[DEBUG] Refreshing available slots after booking for date: ${formattedDate}`);
      
      try {
        const response = await authenticatedAxios.get(`/api/experts/${expertId}/available-hours?date=${formattedDate}&threadId=${threadId}&_t=${new Date().getTime()}`);
        console.log(`[DEBUG] Updated available slots after booking:`, response.data);
        console.log(`[DEBUG] Number of available slots after booking: ${response.data.length}`);
        
        // Check if the booked slot is still in the available slots
        const bookedSlotStillAvailable = response.data.some(
          slot => new Date(slot.start).getTime() === new Date(selectedSolot.start).getTime() && 
                 new Date(slot.end).getTime() === new Date(selectedSolot.end).getTime()
        );
        
        console.log(`[DEBUG] Is booked slot still showing as available? ${bookedSlotStillAvailable ? 'YES (ISSUE!)' : 'NO (CORRECT)'}`);
        
        // Apply frontend filter to remove any slots we know are already booked
        const filteredSlots = filterOutBookedSlots(response.data);
        console.log(`[DEBUG] After frontend filtering: ${response.data.length - filteredSlots.length} slots removed`);
        
        // Drop any slots that are now in the past
        const nowTs2 = Date.now();
        const futureSlots2 = filteredSlots.filter(slot => new Date(slot.start).getTime() > nowTs2);
        console.log(`[DEBUG] After past-slot filtering: ${filteredSlots.length - futureSlots2.length} past slots removed`);
        
        setAvailableSlots(futureSlots2);
      } catch (error) {
        console.log("[DEBUG] Error refreshing available slots:", error);
      }
      
      setLoadFlag(false);
      finishSettingMeeting(false);
    } catch (error) {
      console.log('[DEBUG] Error scheduling meeting:', error);
      
      // Handle specific error codes
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setErrorMessage('Missing required information. Please try again.');
            setLoadFlag(false);
            break;
          case 409:
            // Add the conflicted slot to our local tracking to prevent further attempts
            if (selectedSolot) {
              setBookedSlots(prev => [...prev, selectedSolot]);
              setSelectedSlot(null); // Clear the selected slot to prevent re-submitting
            }
            
            // Just show the error message - the useEffect will handle refreshing after 3 seconds
            setErrorMessage('This time slot is no longer available. Please select another time.');
            setLoadFlag(false);
            break;
          case 500:
            setErrorMessage('Server error. Please try again later.');
            setLoadFlag(false);
            break;
          default:
            setErrorMessage('An error occurred. Please try again.');
            setLoadFlag(false);
        }
      } else {
        setErrorMessage('Network error. Please check your connection and try again.');
        setLoadFlag(false);
      }
    }
  }

  const cancelMeeting = async () => {
    try {
      setLoadFlag(true);
      // Store data we need before cleanup
      const expertIdCopy = expertId;
      
      // First close the window to prevent DOM manipulation issues
      closeWindow();
      
      // Small delay to ensure component cleanup happens first
      setTimeout(async () => {
        try {
          const response = await authenticatedAxios.delete(`/api/experts/meetings/${expertIdCopy}`);
          if (response) {
            finishSettingMeeting(false);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setLoadFlag(false);
        }
      }, 50);
    } catch (error) {
      console.log(error);
      setLoadFlag(false);
    }
  }

  const reschedule = async () => {
    try {
      setLoadFlag(true);
      // Store data we need before cleanup
      const expertIdCopy = expertId;
      
      // First close the window to prevent DOM manipulation issues
      closeWindow();
      
      // Small delay to ensure component cleanup happens first
      setTimeout(async () => {
        try {
          const res = await authenticatedAxios.delete(`/api/experts/meetings/${expertIdCopy}`);
          if (res) {
            finishSettingMeeting(true);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setLoadFlag(false);
        }
      }, 50);
    } catch (error) {
      console.log(error);
      setLoadFlag(false);
    }
  }

  const formatToDate = (dateStr) => {
    const date = new Date(dateStr);
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const month = monthNames[date.getUTCMonth()];
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    return `${month} ${day}, ${year}`;
  }

  return (
    <>
      {loadFlag ? (
        <LoadingContainer>
          {errorMessage && (
            <ErrorMessage style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', zIndex: 10 }}>
              {errorMessage}
            </ErrorMessage>
          )}
          <LogoLoader size={170} message="Loading available times" />
        </LoadingContainer>
      ) : (
        <>
          {localMeetingScheduled ? (
            <MeetingInfoWrapper>
              {/* Status Badge */}
              <MeetingHeader>
                <div className="status-badge">
                  <span className="status-dot"></span>
                  Meeting Scheduled
                </div>
              </MeetingHeader>

              {/* Meeting Details Card */}
              <MeetingInfoContainer>
                {/* Date Section */}
                <MeetingDateCard>
                  <div className="calendar-icon">
                    <span className="month">
                      {new Date(meetingInfo.startTime).toLocaleString('en-US', { month: 'short' }).toUpperCase()}
                    </span>
                    <span className="day">
                      {new Date(meetingInfo.startTime).getDate()}
                    </span>
                  </div>
                  <div className="date-details">
                    <div className="weekday">
                      {new Date(meetingInfo.startTime).toLocaleString('en-US', { weekday: 'long' })}
                    </div>
                    <div className="full-date">
                      {formatToDate(meetingInfo.startTime)}
                    </div>
                  </div>
                </MeetingDateCard>

                {/* Time Section */}
                <MeetingTimeRow>
                  <div className="time-icon">🕐</div>
                  <div className="time-content">
                    <div className="time-block">
                      <div className="label">Start</div>
                      <div className="time">{formatToLocalTime(meetingInfo.startTime)}</div>
                    </div>
                    <span className="time-separator">→</span>
                    <div className="time-block">
                      <div className="label">End</div>
                      <div className="time">{formatToLocalTime(meetingInfo.endTime)}</div>
                    </div>
                  </div>
                  <div className="duration-badge">
                    {(() => {
                      const start = new Date(meetingInfo.startTime);
                      const end = new Date(meetingInfo.endTime);
                      const durationMs = end - start;
                      const durationMins = Math.round(durationMs / (1000 * 60));
                      return durationMins >= 60 
                        ? `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
                        : `${durationMins} min`;
                    })()}
                  </div>
                </MeetingTimeRow>

                {/* Quick Actions */}
                <MeetingQuickActions>
                  <QuickActionButton onClick={() => window.location.href = `/conversations/${threadId}`}>
                    <span className="icon">💬</span>
                    Go to Chat
                  </QuickActionButton>
                </MeetingQuickActions>
              </MeetingInfoContainer>

              {/* Action Buttons */}
              <MeetingInfoButtonsContainer>
                <CancelButton onClick={cancelMeeting}>
                  <span className="icon">✕</span>
                  Cancel Meeting
                </CancelButton>
                <RescheduleButton onClick={reschedule}>
                  <span className="icon">📅</span>
                  Reschedule
                </RescheduleButton>
              </MeetingInfoButtonsContainer>
            </MeetingInfoWrapper>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              padding: '24px',
              minHeight: '450px',
              borderRadius: '20px',
              position: 'relative',
              overflow: 'visible',
              margin: '0 auto',
            }}>
              {errorMessage && (
                <ErrorMessage>
                  {errorMessage}
                </ErrorMessage>
              )}
              
              {/* Header Bar */}
              <HeaderBar>
                <div className="title">Select a Time</div>
                <RefreshButton onClick={refreshAvailableSlots}>
                  Refresh
                </RefreshButton>
              </HeaderBar>
              
              <CalendarAndSlotsWrapper>
                {/* Calendar Panel */}
                <CalendarContainer>
                  <div className='calendar-control-section'>
                    <CalendarComponent
                      ref={calendarRef}
                      change={onchange}
                      min={new Date()}
                    />
                  </div>
                </CalendarContainer>
                
                {/* Desktop Slots Display */}
                <DesktopSlotsContainer>
                  <AvailableTimesTitle>
                    <span className="title-text">Available Times</span>
                    {availableSlots.length > 0 && (
                      <span className="slot-count">{availableSlots.length} slots</span>
                    )}
                  </AvailableTimesTitle>
                  
                  <Container $showSlots={availableSlots.length > 0 || true}>
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot, index) => (
                        <Slot
                          key={`${slot.start}-${slot.end}`}
                          onClick={() => timeSelected(slot)}
                          selected={isSameSlot(selectedSolot, slot)}
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="time-display">
                            <span className="time-label">From</span>
                            <span className="time-value">{formatToLocalTime(slot.start)}</span>
                            <span className="time-separator">→</span>
                            <span className="time-label">To</span>
                            <span className="time-value">{formatToLocalTime(slot.end)}</span>
                          </div>
                        </Slot>
                      ))
                    ) : (
                      <NoSlotsMessage>
                        <span className="icon">📅</span>
                        <span className="message">No available slots for this date.<br/>Try selecting another day.</span>
                      </NoSlotsMessage>
                    )}
                  </Container>
                  
                  {/* Desktop Inline Confirmation */}
                  <DesktopConfirmPanel $show={selectedSolot !== null}>
                    <div className="confirm-header">
                      ✓ Ready to Book
                    </div>
                    {selectedSolot && (
                      <>
                        <div className="confirm-details">
                          <strong>{formatToLocalDate(selectedSolot.start)}</strong><br/>
                          {formatToLocalTime(selectedSolot.start)} - {formatToLocalTime(selectedSolot.end)}
                        </div>
                        <ButtonsContainer>
                          <ConfirmButton disabled={loadFlag} onClick={confirmDate}>
                            Confirm Booking
                          </ConfirmButton>
                          <CancelConfirmButton disabled={loadFlag} onClick={cancelSlotSelection}>
                            Cancel
                          </CancelConfirmButton>
                        </ButtonsContainer>
                      </>
                    )}
                  </DesktopConfirmPanel>
                </DesktopSlotsContainer>

                {/* Mobile Slots Display - Accordion Style */}
                <MobileSlotsSection>
                  <MobileAccordionHeader onClick={() => setMobileAccordionOpen(!mobileAccordionOpen)}>
                    <AccordionTitle>
                      <span className="title">
                        Available Times
                        {availableSlots.length > 0 && (
                          <span className="slot-count">{availableSlots.length}</span>
                        )}
                      </span>
                      <span className="subtitle">
                        {selectedValue ? `For ${selectedValue}` : 'Select a date first'}
                      </span>
                    </AccordionTitle>
                    <AccordionChevron $isOpen={mobileAccordionOpen}>
                      ▼
                    </AccordionChevron>
                  </MobileAccordionHeader>
                  
                  <MobileAccordionContent $isOpen={mobileAccordionOpen}>
                    <Container $showSlots={true}>
                      {availableSlots.length > 0 ? (
                        availableSlots.map((slot, index) => (
                          <Slot
                            key={`mobile-${slot.start}-${slot.end}`}
                            onClick={() => timeSelected(slot)}
                            selected={isSameSlot(selectedSolot, slot)}
                          >
                            <div className="time-display">
                              <span className="time-value">{formatToLocalTime(slot.start)}</span>
                              <span className="time-separator">→</span>
                              <span className="time-value">{formatToLocalTime(slot.end)}</span>
                            </div>
                          </Slot>
                        ))
                      ) : (
                        <NoSlotsMessage>
                          <span className="icon">📅</span>
                          <span className="message">No slots available</span>
                        </NoSlotsMessage>
                      )}
                    </Container>
                  </MobileAccordionContent>
                </MobileSlotsSection>
              </CalendarAndSlotsWrapper>
              
              {/* Fixed Bottom Confirmation Bar (Mobile Only) */}
              <FixedConfirmBar $show={selectedSolot !== null}>
                <ConfirmBarContent>
                  <ConfirmBarInfo>
                    {selectedSolot && (
                      <>
                        <span className="date">{formatToLocalDate(selectedSolot.start)}</span>
                        <span className="time">{formatToLocalTime(selectedSolot.start)} - {formatToLocalTime(selectedSolot.end)}</span>
                      </>
                    )}
                  </ConfirmBarInfo>
                  <ConfirmBarActions>
                    <ConfirmBarButton onClick={cancelSlotSelection}>
                      ✕
                    </ConfirmBarButton>
                    <ConfirmBarButton $primary onClick={confirmDate} disabled={loadFlag}>
                      Confirm
                    </ConfirmBarButton>
                  </ConfirmBarActions>
                </ConfirmBarContent>
              </FixedConfirmBar>
            </div>
          )}
        </>
      )}
    </>
  );
};

// export default WeeklyCalendar;



